import type { BeforeMode } from '@et/ui';

/**
 * O estado de investigacao vive na URL, nao em memoria.
 *
 * Tres consequencias que interessam: sobrevive a um refresh, e partilhavel, e
 * mudar de lingua e so trocar o prefixo do caminho. §122 pede que uma
 * investigacao seja reproduzivel — comeca aqui.
 */
export interface InvestigationFilters {
  readonly religion: readonly string[];
  readonly topic: readonly string[];
  readonly type: readonly string[];
  readonly before: number | null;
  readonly beforeMode: BeforeMode;
  readonly withinYears: number | null;
  readonly anchorYear: number | null;
}

export const EMPTY_FILTERS: InvestigationFilters = {
  religion: [], topic: [], type: [],
  before: null, beforeMode: 'possibly', withinYears: null, anchorYear: null,
};

const list = (v: string | null): string[] =>
  v === null || v.length === 0 ? [] : v.split(',').map((s) => s.trim()).filter(Boolean);

const int = (v: string | null): number | null => {
  if (v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

export function parseFilters(params: URLSearchParams): InvestigationFilters {
  const mode = params.get('beforeMode');
  return {
    religion: list(params.get('religion')),
    topic: list(params.get('topic')),
    type: list(params.get('type')),
    before: int(params.get('before')),
    beforeMode: mode === 'certainly' ? 'certainly' : 'possibly',
    withinYears: int(params.get('withinYears')),
    anchorYear: int(params.get('anchorYear')),
  };
}

/** Serializa omitindo o que esta por omissao: a URL fica curta e legivel. */
export function serialiseFilters(f: InvestigationFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.religion.length > 0) p.set('religion', f.religion.join(','));
  if (f.topic.length > 0) p.set('topic', f.topic.join(','));
  if (f.type.length > 0) p.set('type', f.type.join(','));
  if (f.before !== null) {
    p.set('before', String(f.before));
    if (f.beforeMode === 'certainly') p.set('beforeMode', 'certainly');
  }
  if (f.withinYears !== null) {
    p.set('withinYears', String(f.withinYears));
    if (f.anchorYear !== null) p.set('anchorYear', String(f.anchorYear));
  }
  return p;
}

export const toggle = (values: readonly string[], value: string): string[] =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

export const isEmpty = (f: InvestigationFilters): boolean =>
  serialiseFilters(f).toString().length === 0;

/** Converte para os parametros que a API entende. */
export function toApiQuery(f: InvestigationFilters): URLSearchParams {
  const p = serialiseFilters(f);
  if (f.before !== null && f.beforeMode === 'possibly') p.set('beforeMode', 'possibly');
  return p;
}
